// Carer detail page — decrypted PII, profile, onboarding, performance metrics.

import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { JSX } from "react";
import { apiFetch } from "../lib/api.js";
import { Shell } from "../components/Shell.js";
import { CarerScheduleDrawer } from "../components/CarerScheduleDrawer.js";

interface Carer {
  _id: string;
  full_name: string;
  primary_phone: string;
  tier: 1 | 2 | 3;
  skills: string[];
  city: string;
  coverage_pincodes: string[];
  gender: "male" | "female" | "other" | "not_disclosed";
  languages: string[];
  languages_spoken: string[];
  photo_url: string | null;
  id_verified: boolean;
  background_verified_date: string | null;
  agency_id: string | null;
  qualifications: string[];
  years_experience: number | null;
  training_completed: string[];
  specialist_skills: string[];
  is_active: boolean;
  is_available: boolean;
  availability_updated_at: string | null;
  availability_schedule: Partial<Record<"mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun", { start: string; end: string }[]>>;
  onboarding_date: string;
  contract_expiry_date: string | null;
  onboarding_checklist: string[];
  rating_avg: number | null;
  rating_count: number;
  csat_score_90d: number | null;
  on_time_rate: number | null;
  report_submission_rate: number | null;
  no_show_count: number;
  compliance_rate: number | null;
}

const TIER_BADGE: Record<number, string> = {
  1: "bg-blue-100 text-blue-700",
  2: "bg-amber-100 text-amber-700",
  3: "bg-purple-100 text-purple-700",
};

function InfoRow({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex gap-2">
      <span className="w-44 text-xs text-slate-500 shrink-0">{label}</span>
      <span className="text-sm text-slate-800">{value || "—"}</span>
    </div>
  );
}

function StatusPill({ active, label }: { active: boolean; label: string }): JSX.Element {
  return (
    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${active ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"}`}>
      {label}: {active ? "Yes" : "No"}
    </span>
  );
}

function fmtDate(s: string | null | undefined): string {
  if (!s) return "—";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString("en-IN", { dateStyle: "medium" });
}

function fmtPct(v: number | null | undefined): string {
  if (v == null) return "—";
  return `${Math.round(v * 100)}%`;
}

function CarerInfoCard({ c }: { c: Carer }): JSX.Element {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5 mb-4">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          {c.photo_url && (
            <img src={c.photo_url} alt="" className="h-12 w-12 rounded-full object-cover border border-slate-200" />
          )}
          <div>
            <h2 className="text-xl font-semibold text-slate-900">{c.full_name}</h2>
            <p className="text-sm text-slate-500 mt-0.5">{c.city || "—"}</p>
          </div>
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${TIER_BADGE[c.tier] ?? "bg-slate-100 text-slate-600"}`}>
          Tier {c.tier}
        </span>
      </div>
      <div className="space-y-2">
        <InfoRow label="Primary phone" value={c.primary_phone} />
        <InfoRow label="Skills" value={c.skills.join(", ")} />
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        <StatusPill active={c.is_active} label="Active" />
        <StatusPill active={c.is_available} label="Available" />
      </div>
    </div>
  );
}

function ProfileCard({ c }: { c: Carer }): JSX.Element {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5 mb-4">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Profile</p>
      <div className="space-y-2">
        <InfoRow label="Gender" value={c.gender.replace("_", " ")} />
        <InfoRow label="Languages" value={c.languages_spoken.join(", ") || c.languages.join(", ")} />
        <InfoRow label="Qualifications" value={c.qualifications.join(", ")} />
        <InfoRow label="Years experience" value={c.years_experience != null ? String(c.years_experience) : ""} />
        <InfoRow label="Training" value={c.training_completed.join(", ")} />
        <InfoRow label="Specialist skills" value={c.specialist_skills.join(", ")} />
        <InfoRow label="Coverage pincodes" value={c.coverage_pincodes.join(", ")} />
        <InfoRow label="Agency" value={c.agency_id ?? ""} />
      </div>
    </div>
  );
}

function OnboardingCard({ c }: { c: Carer }): JSX.Element {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5 mb-4">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Onboarding</p>
      <div className="space-y-2">
        <InfoRow label="Onboarding date" value={fmtDate(c.onboarding_date)} />
        <InfoRow label="Contract expiry" value={fmtDate(c.contract_expiry_date)} />
        <InfoRow label="ID verified" value={c.id_verified ? "Yes" : "No"} />
        <InfoRow label="Background check" value={fmtDate(c.background_verified_date)} />
        <InfoRow
          label="Onboarding checklist"
          value={c.onboarding_checklist.length ? `${c.onboarding_checklist.length} items` : ""}
        />
      </div>
    </div>
  );
}

function MetricRow({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between border-b border-slate-100 last:border-0 py-2">
      <span className="text-sm text-slate-600">{label}</span>
      <span className="text-base font-semibold text-slate-900 tabular-nums">{value}</span>
    </div>
  );
}

function PerformanceCard({ c }: { c: Carer }): JSX.Element {
  const rating = c.rating_avg != null ? `★ ${c.rating_avg.toFixed(1)}` : "—";
  const reviewSubtitle = c.rating_count > 0 ? `(${c.rating_count} reviews)` : "(no reviews yet)";
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5 mb-4">
      <div className="flex items-baseline justify-between mb-2">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Performance</p>
        <span className="text-xs text-slate-400">{reviewSubtitle}</span>
      </div>
      <div>
        <MetricRow label="Rating" value={rating} />
        <MetricRow label="CSAT (90d)" value={fmtPct(c.csat_score_90d)} />
        <MetricRow label="On time" value={fmtPct(c.on_time_rate)} />
        <MetricRow label="Reports filed on time" value={fmtPct(c.report_submission_rate)} />
        <MetricRow label="No-shows" value={String(c.no_show_count)} />
        <MetricRow label="Compliance" value={fmtPct(c.compliance_rate)} />
      </div>
    </div>
  );
}

export function CarerDetailPage(): JSX.Element {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [schedOpen, setSchedOpen] = useState(false);
  const { data: carer, isLoading, isError } = useQuery<Carer>({
    queryKey: ["carer", id],
    queryFn: () => apiFetch<Carer>(`/carers/${id}`),
  });

  if (isLoading) return <Shell><p className="p-8 text-sm text-slate-400" aria-busy="true">Loading…</p></Shell>;
  if (isError || !carer) return <Shell><p className="p-8 text-sm text-red-600">Carer not found.</p></Shell>;

  return (
    <Shell>
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/carers", { state: { highlightId: carer._id } })} className="text-sm text-navy-700 hover:underline">
              ← Carers
            </button>
            <h1 className="text-xl font-semibold text-slate-900">{carer.full_name}</h1>
          </div>
          <button
            onClick={() => setSchedOpen(true)}
            className="text-sm bg-navy-700 text-white px-3 py-1.5 rounded-lg hover:bg-navy-800 transition-colors"
          >
            View schedule
          </button>
        </div>
        <CarerInfoCard c={carer} />
        <ProfileCard c={carer} />
        <OnboardingCard c={carer} />
        <PerformanceCard c={carer} />
      </div>
      {schedOpen && (
        <CarerScheduleDrawer
          carerId={carer._id}
          carerName={carer.full_name}
          initialSchedule={carer.availability_schedule ?? {}}
          onClose={() => setSchedOpen(false)}
        />
      )}
    </Shell>
  );
}
